import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Admin, OutreachProspect, OutreachProspectPage, OutreachStage } from "@lumika/shared";
import { OutreachStageSchema } from "@lumika/shared";
import { toast } from "@lumika/ui";
import { adminApi } from "../../../api/admin";
import { STAGE_LABELS, STAGE_ORDER, buildAdminNames } from "../outreach-ui";
import { groupByStage, sortColumn } from "./board-utils";
import { BoardColumn } from "./BoardColumn";
import { ProspectCardOverlay } from "./ProspectCard";

type Props = {
  prospects: OutreachProspect[];
  admins: Admin[];
  onCardClick: (prospect: OutreachProspect) => void;
};

/**
 * Kanban board: one column per pipeline stage, horizontal scroll on narrow
 * viewports. Drag a card to another column to PATCH its stage; assign an owner
 * inline from each card.
 */
export function OutreachBoard({ prospects, admins, onCardClick }: Props) {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeProspect = useMemo(
    () => (activeId ? (prospects.find((p) => p.id === activeId) ?? null) : null),
    [activeId, prospects],
  );

  // PointerSensor: distance ≥5px before drag starts so a plain click still fires.
  // TouchSensor: 200ms dwell to distinguish tap (opens panel) from horizontal swipe.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const ownerNames = useMemo(() => buildAdminNames(admins), [admins]);

  const buckets = useMemo(() => {
    const grouped = groupByStage(prospects);
    return Object.fromEntries(
      STAGE_ORDER.map((s) => [s, sortColumn(grouped[s])]),
    ) as Record<OutreachStage, OutreachProspect[]>;
  }, [prospects]);

  // Optimistically patch a prospect across every cached list page, returning
  // the prior snapshots so onError can roll back. Shared by the stage-drag and
  // owner-assign mutations.
  async function applyOptimisticPatch(id: string, patch: Partial<OutreachProspect>) {
    await qc.cancelQueries({ queryKey: ["outreach", "prospects"] });
    const snapshots = qc.getQueriesData<OutreachProspectPage>({
      queryKey: ["outreach", "prospects"],
    });
    for (const [queryKey] of snapshots) {
      qc.setQueryData<OutreachProspectPage>(queryKey, (old) =>
        old ? { ...old, items: old.items.map((p) => (p.id === id ? { ...p, ...patch } : p)) } : old,
      );
    }
    return snapshots;
  }

  function restoreSnapshots(snapshots: Awaited<ReturnType<typeof applyOptimisticPatch>>) {
    for (const [queryKey, data] of snapshots) qc.setQueryData(queryKey, data);
  }

  const stageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: OutreachStage }) =>
      adminApi.updateOutreachProspect(id, { stage }),
    onMutate: ({ id, stage }) => applyOptimisticPatch(id, { stage }).then((snapshots) => ({ snapshots })),
    onError: (_err, _vars, context) => {
      if (context?.snapshots) restoreSnapshots(context.snapshots);
      toast.error("Couldn't move prospect — please try again.");
    },
    onSettled: async () => {
      // Re-sync from the server so the badge / summary / list all agree.
      await qc.invalidateQueries({ queryKey: ["outreach"] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, ownerAdminId }: { id: string; ownerAdminId: string | null }) =>
      adminApi.updateOutreachProspect(id, { ownerAdminId }),
    onMutate: ({ id, ownerAdminId }) =>
      applyOptimisticPatch(id, { ownerAdminId }).then((snapshots) => ({ snapshots })),
    onError: (_err, _vars, context) => {
      if (context?.snapshots) restoreSnapshots(context.snapshots);
      toast.error("Couldn't reassign prospect — please try again.");
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ["outreach"] });
    },
  });

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const newStage = OutreachStageSchema.safeParse(over.id);
    if (!newStage.success) return;

    // Resolve the prospect's CURRENT stage from props, not the snapshot captured
    // on the draggable at drag-start — a prior move's refetch can make that stale
    // and cause a real cross-column drop to be swallowed as a no-op.
    const prospect = prospects.find((p) => p.id === active.id);
    if (!prospect) return;
    if (prospect.stage === newStage.data) return; // no-op: dropped onto same column

    stageMutation.mutate({ id: prospect.id, stage: newStage.data });
  }

  function handleAssign(prospectId: string, ownerAdminId: string | null) {
    assignMutation.mutate({ id: prospectId, ownerAdminId });
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event: DragStartEvent) => setActiveId(String(event.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {/* Fill the board area's height so columns scroll internally; scroll the
          row horizontally on narrow viewports (tablets / phones). */}
      <div className="flex h-full gap-4 overflow-x-auto pb-1">
        {STAGE_ORDER.map((stage) => (
          <BoardColumn
            key={stage}
            stage={stage}
            label={STAGE_LABELS[stage]}
            prospects={buckets[stage]}
            ownerNames={ownerNames}
            admins={admins}
            onCardClick={onCardClick}
            onAssign={handleAssign}
          />
        ))}
      </div>

      {/* Lifted clone under the pointer; no snap-back so the card simply lands
          in its new column after an optimistic move. */}
      <DragOverlay dropAnimation={null}>
        {activeProspect ? <ProspectCardOverlay prospect={activeProspect} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
